import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import {
  Avatar,
  Button,
  Clickable,
  Col,
  Logo,
  Modal,
  Row,
  Spinner2,
  TermsAgreement,
  Text
} from 'components';
import { CallToActionLogin, SignupWithEmail, SignupWithSocials } from './Auth';

// Utils
import { firebase, getRoleBgColor, openMobileAppLink } from 'core';
import { Api, theme } from 'lib';

const AUTH_STEPS = {
  SIGN_UP_WITH_SOCIALS: 'SIGN_UP_WITH_SOCIALS',
  SIGN_UP_WITH_EMAIL: 'SIGN_UP_WITH_EMAIL',
  CALL_TO_ACTION_LOGIN: 'CALL_TO_ACTION_LOGIN'
};

// `fancam` is the FanCam Challenge section's address; see `CTA_ALIASES` in `Profile/index.js`.
const ACTION_ALIASES = { fancam: 'fancamchallenge' };

const normalizeActionType = (value) => {
  const normalized = value?.toLowerCase().replace(/[-_]/g, '');
  return ACTION_ALIASES[normalized] ?? normalized;
};

const APP_ONLY_CTA_BUTTON_LABELS = {
  fancamlive: 'callToAction.startFancamLive',
  fancamchallenge: 'callToAction.submitPhotoVideo',
  playtowinpoll: 'callToAction.voteNow',
  playtowinquickquiz: 'callToAction.takeTheQuiz',
  predictscore: 'callToAction.predictScoreBtn',
  rewards: 'callToAction.seeRewards'
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(210, 216, 223, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const LoadingOverlay = () => {
  return (
    <Container>
      <Spinner2 col={theme.SECONDARY} />
    </Container>
  );
};

const CallToActionModal = ({
  isVisible,
  onCloseModal,
  cta,
  otherUser,
  setOtherUser,
  isFollowing,
  isSkipToSignUp,
  setShoutoutSelection,
  isPaymentRequired,
  setPaymentModalVisible,
  deeplinkPath
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useSelector((state) => state.appState);

  const [loadingSocialSignIn, setLoadingSocialSignIn] = useState(false);
  const [isSignupVisible, setIsSignupVisible] = useState(false);
  const [currentAuthStep, setCurrentAuthStep] = useState(AUTH_STEPS.SIGN_UP_WITH_SOCIALS);
  const user = useSelector((state) => state.user);

  const isLoggedIn = user !== null;
  const actionType = normalizeActionType(cta);
  const isAppOnlyCtaFeature = [
    'fancamLive',
    'fancamChallenge',
    'playToWinPoll',
    'playToWinQuickQuiz',
    'predictScore',
    // A team's rewards are a carousel inside the app's team channel, and a campaign email's
    // "See Rewards" button addresses them. On the web the handoff is the whole answer.
    'rewards'
  ]
    .map(normalizeActionType)
    .includes(actionType);
  const shouldShowAuthFlow = isSignupVisible || isSkipToSignUp;

  const callToAction = {
    follow: {
      label: t('callToAction.actionFollow'),
      btnLabel: t('common.follow'),
      action: follow
    },
    subscribe: {
      label: t('callToAction.actionSubscribe'),
      btnLabel: t('common.subscribe'),
      action: subscribe
    },
    face2face: {
      label: t('callToAction.actionF2F'),
      btnLabel: t('common.face2face'),
      action: null
    },
    adplayaz: {
      label: t('common.adPlayaz'),
      description: t('common.featureComingSoon'),
      btnLabel: t('common.okay'),
      action: () => null
    },
    fancamlive: {
      label: t('callToAction.actionFancamLive'),
      btnLabel: t('common.activateFancamLive'),
      action: null
    },
    fancamchallenge: {
      label: t('callToAction.actionFancamChallenge'),
      btnLabel: t('common.activateFancamChallenge'),
      action: null
    },
    playtowinpoll: {
      label: t('callToAction.actionPlayToWinPoll'),
      btnLabel: t('common.playToWinPoll'),
      action: null
    },
    playtowinquickquiz: {
      label: t('callToAction.actionPlayToWinQuickQuiz'),
      btnLabel: t('common.playToWinQuickQuiz'),
      action: null
    },
    predictscore: {
      label: t('callToAction.actionPredictScore'),
      btnLabel: t('common.predictScore'),
      action: null
    },
    rewards: {
      label: t('callToAction.actionRewards'),
      btnLabel: t('callToAction.seeRewards'),
      action: null
    },
    requestshoutout: {
      label: t('callToAction.actionRequestShoutout'),
      btnLabel: t('requestShoutout.requestAShoutout'),
      action: () => {
        firebase.logEvent('web_cta_request_shoutout_clicked');

        // if (isPaymentRequired) {
        //   setPaymentModalVisible(true);
        // } else {
        //   setShoutoutSelection({ preSelect: 0, preSelectPage: 'shoutout' });
        // }

        setShoutoutSelection({ preSelect: 0, preSelectPage: 'shoutout' });
        onCloseModal();
      }
    },
    sendshoutout: {
      label: t('callToAction.actionSendShoutout'),
      btnLabel: t('callToAction.sendAShoutout'),
      action: () => {
        firebase.logEvent('web_cta_send_shoutout_clicked');

        // if (isPaymentRequired) {
        //   setPaymentModalVisible(true);
        // } else {
        //   setShoutoutSelection({ preSelect: 1, preSelectPage: 'sendShoutout' });
        // }

        onCloseModal();
        navigate(`/shoutout/${otherUser?.username}`);
      }
    }
  };
  const followUpDetails = { actionType, otherUser, action: callToAction[actionType]?.action };

  async function follow() {
    const res = await Api.post('/users/follow', { userId: otherUser._id }, 'follow');
    if (!res) return;

    dispatch({
      type: 'FOLLOW',
      payload: otherUser._id
    });

    setOtherUser((prevState) => {
      return {
        ...prevState,
        followersCount: prevState.followersCount + 1
      };
    });

    firebase.logEvent('web_cta_follow_clicked');

    onCloseModal();
  }

  // async function unfollow() {
  //   const res = await Api.post('/users/unfollow', { userId: otherUser._id }, 'follow');
  //   if (!res) return;

  //   dispatch({
  //     type: 'UNFOLLOW',
  //     payload: otherUser._id,
  //   });

  //   setOtherUser(prevState => {
  //     return {
  //       ...prevState,
  //       followersCount: prevState.followersCount - 1,
  //     };
  //   });
  // }

  function subscribe() {
    firebase.logEvent('web_cta_subscribe_clicked');

    navigate(`/subscribe/${otherUser._id}`);
  }

  async function handleTriggerCTA() {
    if (isAppOnlyCtaFeature) {
      const shouldOpenApp = [
        'fancamlive',
        'fancamchallenge',
        'playtowinpoll',
        'playtowinquickquiz',
        'predictscore',
        'rewards'
      ].includes(actionType);
      openMobileAppLink(shouldOpenApp ? deeplinkPath : undefined);
      onCloseModal();
      return;
    }

    if (typeof callToAction[actionType]?.action !== 'function') return;

    if (!isLoggedIn) {
      setIsSignupVisible(true);
      setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_SOCIALS);
      return;
    }

    // when user is logged in
    await callToAction[actionType]?.action();
  }

  function handleLoadingSocialSignIn() {
    setLoadingSocialSignIn((prevState) => !prevState);
  }

  const renderSignupFooter = () => {
    return (
      <>
        <TermsAgreement maxWid={isMobile ? '100%' : '70%'} />

        <Clickable
          marg="20px 0 12px 0"
          onClick={() => {
            setCurrentAuthStep(AUTH_STEPS.CALL_TO_ACTION_LOGIN);
          }}
        >
          <Text b2>
            {t('auth.alreadyHaveAccount')}{' '}
            <Text spanned b2 med>
              {t('auth.logIn')}
            </Text>
          </Text>
        </Clickable>
      </>
    );
  };

  const renderCurrentStep = () => {
    switch (currentAuthStep) {
      case AUTH_STEPS.SIGN_UP_WITH_SOCIALS:
        return (
          <>
            <SignupWithSocials
              isMobile={isMobile}
              handleLoadingSocialSignIn={handleLoadingSocialSignIn}
              onHandleSignUpWithEmail={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_EMAIL)}
              followUpDetails={followUpDetails}
            />

            {renderSignupFooter()}
          </>
        );
      case AUTH_STEPS.SIGN_UP_WITH_EMAIL:
        return (
          <>
            <SignupWithEmail
              otherUser={otherUser}
              actionType={cta}
              onHandleGoBack={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_SOCIALS)}
              followAthlete={cta === 'follow' ? follow : null}
            />

            {renderSignupFooter()}
          </>
        );
      case AUTH_STEPS.CALL_TO_ACTION_LOGIN:
        return (
          <CallToActionLogin
            otherUser={otherUser}
            actionType={cta}
            setCurrentAuthStep={setCurrentAuthStep}
            followAthlete={cta === 'follow' ? follow : null}
            authSteps={AUTH_STEPS}
            followUpDetails={followUpDetails}
            handleLoadingSocialSignIn={handleLoadingSocialSignIn}
          />
        );

      default:
        return (
          <SignupWithSocials
            isMobile={isMobile}
            handleLoadingSocialSignIn={handleLoadingSocialSignIn}
            navigate={navigate}
            followUpDetails={followUpDetails}
          />
        );
    }
  };

  const renderAuth = () => {
    return (
      <Col centerAll wid="100%" minWid={!isMobile ? '500px' : '100%'}>
        {/* User Profile */}
        <Col wid="100%" centerAll pad="24px 0" bg2={getRoleBgColor(otherUser?.type)}>
          <Row centerAll wrapFlex noFlex pad="0 12px">
            <Avatar user={otherUser} showVerified />

            {/* MOBILE VIEW for LONG NAMES */}
            {isMobile && otherUser?.name.length > 20 ? (
              <Row marg="12px 0 0 0" noFlex>
                <Text h3 col={'#fff'} center>
                  {otherUser?.name}
                </Text>

                <Text c1 med col={'#fff'} center>
                  @{otherUser.username}
                </Text>
              </Row>
            ) : (
              <>
                <Col marg="0 0 0 12px" center wrap={+true}>
                  <Text h3 col={'#fff'} style={{ maxWidth: '325px' }}>
                    {otherUser?.name}
                  </Text>

                  <Text c1 med col={'#fff'}>
                    @{otherUser.username}
                  </Text>
                </Col>
              </>
            )}
          </Row>
        </Col>

        {/* Display LOGIN or Sign Up with SOCIALS/EMAIL */}
        {renderCurrentStep()}

        <Logo wid="156px" />
      </Col>
    );
  };

  return (
    <Modal
      visible={isVisible}
      closeModal={onCloseModal}
      wid={isMobile ? '325px !important ' : 'auto !important'}
      contentArea2MaxHt="84vh"
      closeIconFill={isSignupVisible || isSkipToSignUp ? '#fff' : theme.GREY_60}
    >
      <Col center wid="100%" pad="0 0 24px 0" marg="0 auto" boxSizing="border-box">
        {loadingSocialSignIn && <LoadingOverlay />}

        {shouldShowAuthFlow ? (
          renderAuth()
        ) : (
          <Col
            pad={isMobile ? '46px 34px 12px 34px' : '42px 42px 20px 42px'}
            wid="100%"
            boxSizing="border-box"
          >
            <Text h4={isMobile} h3={!isMobile} center pad="1rem 0">
              {callToAction[actionType]?.label}
            </Text>

            {actionType === 'adplayaz' && (
              <Text b2 big center marg="16px 0 16px 0">
                {callToAction[actionType]?.description}
              </Text>
            )}

            <Col gap="12px" wid={'100%'} marg="1rem 0 0 0">
              {!isAppOnlyCtaFeature ? (
                <Button
                  text={callToAction[actionType]?.btnLabel}
                  big
                  app
                  reverse
                  bg={theme.SECONDARY}
                  pad="0 15.5px 0 16.5px"
                  action="follow"
                  gridColSpan={1}
                  icon={!isLoggedIn && isFollowing ? 'doubleCheck' : ''}
                  iconSize="20"
                  onClick={handleTriggerCTA}
                />
              ) : (
                <Button
                  text={
                    APP_ONLY_CTA_BUTTON_LABELS[actionType]
                      ? t(APP_ONLY_CTA_BUTTON_LABELS[actionType])
                      : t('common.openInApp')
                  }
                  big
                  app
                  reverse
                  bg={theme.SECONDARY}
                  pad="0 15.5px 0 16.5px"
                  onClick={handleTriggerCTA}
                />
              )}

              {actionType !== 'adplayaz' && (
                <Button big bordered text={t('common.cancel')} onClick={onCloseModal} />
              )}
            </Col>
          </Col>
        )}
      </Col>
    </Modal>
  );
};

export default CallToActionModal;
